import { ApiError, api, flushQueue, queued } from "../api/client";
import { localDay } from "../lib/dates";
import { EpisodeKey, knownSeason, logSeason } from "../lib/episodes";
import { moveLabel } from "../lib/library";
import { TimelinePage, WantGroup, YearPage } from "../types/Home";
import { Favorite, PartReview } from "../types/Social";
import Tracking, { Status, Visibility } from "../types/Tracking";
import UpNext from "../types/UpNext";
import { WorkType } from "../types/Work";
import { changed } from "./changes";
import { Topic } from "./changes";
import { prefetch } from "./prefetch";
import { SAVED_OFFLINE, savedOffline } from "./save";
import { showToast } from "./toast";
import { useApi } from "./useApi";

// The shelf's writes: every change to someone's own library goes through
// here, sent `queued`, so offline it waits and syncs once back (ADR 0005;
// only the shelf, the owner's call in plan 019). A page shows the change and
// says what happened; how each is sent is this module's.

export const getEntry = (id: number) => api.get<Tracking>(`/library/${id}`);

export const addEntry = (entry: {
  workId: number;
  status: Status;
  on?: string;
}) => queued.post<Tracking>("/library", entry);

// Another edition of a work already in the library, as an entry of its
// own, on this exact record (plan 013 §2.5): only the work's page's "Add
// this edition" asks for one. Without it, adding any record of a work the
// library holds is refused.
export const addEdition = (workId: number, status: Status) =>
  queued.post<Tracking>("/library", { workId, status, anotherEdition: true });

// Which edition an entry is: another record of its work, or none to clear it
// back to "Any edition", the work's original (plan 013 §2.5).
export const setEdition = (id: number, editionId: number | null) =>
  queued.post<Tracking>(`/library/${id}/edition`, { editionId });

export const patchEntry = (
  id: number,
  changes: Partial<Tracking> & { on?: string },
) => queued.patch<Tracking>(`/library/${id}`, changes);

// Reviews and visibility have their own endpoint (and rate limit, ADR 0008)
// so publishing is an explicit act rather than a side effect of a PATCH.
export const putReview = (
  id: number,
  changes: {
    review?: string | null;
    visibility?: Visibility;
    spoiler?: boolean;
  },
) => queued.put<Tracking>(`/library/${id}/review`, changes);

export const removeEntry = (id: number) => queued.del(`/library/${id}`);

// A season's or an episode's own rating and review (ADR 0030, Decision 6):
// written in place, a field left out kept as it was. Clearing both the
// rating and the review deletes it, and the API answers with none.
export const putPartReview = async (part: {
  workId: number;
  season: number;
  number?: number | null;
  rating?: number | null;
  review?: string | null;
  spoiler?: boolean;
}): Promise<PartReview | null> =>
  (await queued.put<PartReview | undefined>("/library/part-reviews", part)) ??
  null;

export const addLog = (
  id: number,
  log: { startedAt: string | null; finishedAt: string | null },
) => queued.post<Tracking>(`/library/${id}/logs`, log);

export const removeLog = (id: number, logId: number) =>
  queued.del<Tracking>(`/library/${id}/logs/${logId}`);

// Another time through a completed work (a rewatch): begun, finished as a
// finish logged from the day it began, or stopped with none.
export const again = (id: number, method: "start" | "finish" | "stop") => {
  const path = `/library/${id}/again`;
  return method === "stop"
    ? queued.del<Tracking>(path)
    : queued.post<Tracking>(method === "finish" ? `${path}/finish` : path, {});
};

export const saveFavorites = (ids: number[]) =>
  queued.put<Favorite[]>("/library/favorites", { ids });

// A shelf's order, as dragged.
export const reorderShelf = (ids: number[]) =>
  queued.post("/library/reorder", { ids });

// Continue's cards (ADR 0030 names a show's season with its work).
export const skipUpNext = (workId: number, season?: number | null) =>
  queued.post("/library/up-next/skip", { workId, ...knownSeason(season) });
export const pinUpNext = (workId: number, pinned: boolean) =>
  queued.post("/library/up-next/pin", { workId, pinned });
export const hideUpNext = (workId: number, hidden: boolean) =>
  queued.post("/library/up-next/hide", { workId, hidden });

// One episode watched (adding its season to the library if need be), or
// taken back.
export const watch = (episode: EpisodeBody) =>
  queued.post<Tracking>("/library/watch", episode);
export const unwatch = (episode: EpisodeBody) =>
  queued.post<Tracking>("/library/unwatch", episode);

type EpisodeBody = {
  workId: number;
  season?: number;
  number: number;
  on?: string;
};

/** Whether `err` says what it changed is already gone (a 404). */
export const gone = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 404;

/** What a page says when a shelf change couldn't be saved. */
export const unsaved = (err: unknown): string =>
  savedOffline(err) ? SAVED_OFFLINE : "Couldn't save that — try again.";

// Put one entry back the way it was before a status change. A status change
// can open or close a log server-side (`_apply_status_log`), and patching the
// status back doesn't undo that — it may even open or close another. So patch
// the status first, then reconcile the logs it returns against the snapshot:
// delete any that didn't exist and restore the dates of any that changed. A
// log's range in a season (ADR 0030) is patched with it, so it stays there.
// A time through again it was on (a rewatch) begins again on its day.
export const revertEntry = async (before: Tracking): Promise<void> => {
  const after = await queued.patch<Tracking>(`/library/${before.id}`, {
    status: before.status,
  });
  const previous = new Map(before.logs.map((log) => [log.id, log]));
  await Promise.all(
    after.logs.map((log) => {
      const prev = previous.get(log.id);
      if (!prev) return queued.del(`/library/${before.id}/logs/${log.id}`);
      if (
        prev.startedAt === log.startedAt &&
        prev.finishedAt === log.finishedAt &&
        !!prev.dropped === !!log.dropped
      )
        return undefined;
      return queued.patch(`/library/${before.id}/logs/${log.id}`, {
        startedAt: prev.startedAt,
        finishedAt: prev.finishedAt,
        ...logSeason(prev),
        segmentStart: prev.segmentStart,
        segmentEnd: prev.segmentEnd,
        dropped: !!prev.dropped,
      });
    }),
  );
  if (before.againSince && !after.againSince)
    await queued.post(`/library/${before.id}/again`, {
      on: localDay(before.againSince),
    });
};

// A change to the entries a page shows, made there at once while the
// writes go out behind it: `moveEntries` and `removeEntries` take the
// page's way to show one, so a bulk action shows before its last write is
// in (or, offline, at all), and the refetch after puts right what the
// server did differently.
export type ShelfEdit = (entries: Tracking[]) => Tracking[];

// Move entries to `status`, then offer an undo toast. Entries already there
// are skipped. Failures (e.g. offline, where the client queues the change)
// don't block the rest; every library view refetches either way.
export const moveEntries = async (
  entries: Tracking[],
  status: Status,
  show: (edit: ShelfEdit) => void = () => {},
): Promise<void> => {
  const moving = entries.filter((e) => e.status !== status);
  const before = new Map(moving.map((e) => [e.id, e]));
  show((shown) => shown.map((e) => (before.has(e.id) ? { ...e, status } : e)));
  await Promise.allSettled(
    moving.map((e) => queued.patch(`/library/${e.id}`, { status })),
  );
  changed("shelf");

  const what =
    moving.length === 1 ? `“${moving[0].work?.title}”` : `${moving.length}`;
  showToast(`Moved ${what} to ${moveLabel(status, moving)}`, {
    label: "Undo",
    run: async () => {
      show((shown) => shown.map((e) => before.get(e.id) ?? e));
      await Promise.allSettled(moving.map(revertEntry));
      changed("shelf");
    },
  });
};

// Re-add a removed entry. The server assigns a new id and may auto-open or
// close a log for the status (`_apply_status_log`), so drop whatever logs the
// create returned and re-post the originals. Manual priority isn't restored:
// the entry comes back at the end of its shelf. A log's range in a season
// goes back into it.
export const restoreEntry = async (removed: Tracking): Promise<void> => {
  const body = {
    workId: removed.workId,
    status: removed.status,
    rating: removed.rating,
    notes: removed.notes,
  };
  // One of two entries on a work's records comes back as another edition:
  // the library holds the work still, so a plain add is refused.
  const created = await queued
    .post<Tracking>("/library", body)
    .catch((err: unknown) => {
      if (err instanceof ApiError && err.body?.code === "ALREADY_TRACKED")
        return queued.post<Tracking>("/library", {
          ...body,
          anotherEdition: true,
        });
      throw err;
    });
  await Promise.all(
    created.logs.map((log) =>
      queued.del(`/library/${created.id}/logs/${log.id}`),
    ),
  );
  for (const log of removed.logs)
    await queued.post(`/library/${created.id}/logs`, {
      startedAt: log.startedAt,
      finishedAt: log.finishedAt,
      ...logSeason(log),
      segmentStart: log.segmentStart,
      segmentEnd: log.segmentEnd,
      dropped: !!log.dropped,
    });
};

// Remove entries from the library, then offer an undo toast.
export const removeEntries = async (
  entries: Tracking[],
  show: (edit: ShelfEdit) => void = () => {},
): Promise<void> => {
  const ids = new Set(entries.map((e) => e.id));
  show((shown) => shown.filter((e) => !ids.has(e.id)));
  await Promise.allSettled(entries.map((e) => queued.del(`/library/${e.id}`)));
  changed("shelf");

  const what =
    entries.length === 1 ? `“${entries[0].work?.title}”` : `${entries.length}`;
  showToast(`Removed ${what} from your library`, {
    label: "Undo",
    run: async () => {
      // Back under their old ids until the refetch brings the new ones.
      show((shown) => [...shown.filter((e) => !ids.has(e.id)), ...entries]);
      await Promise.allSettled(entries.map(restoreEntry));
      changed("shelf");
    },
  });
};

// Log one episode as watched today, or on the day `on` names (adding its
// season to the library if needed), or take one back; returns the season's updated entry, or null
// when it didn't save. A show's episode is named by its season and number
// (ADR 0030); a legacy season's by its number alone, as before.
const episodeAction =
  (path: string) =>
  async (
    workId: number,
    { season, number }: EpisodeKey,
    on?: string,
  ): Promise<Tracking | null> => {
    try {
      const updated = await queued.post<Tracking>(path, {
        workId,
        ...knownSeason(season),
        number,
        ...(on && { on }),
      });
      changed("shelf");
      return updated;
    } catch (err) {
      showToast(unsaved(err));
      return null;
    }
  };

export const watchEpisode = episodeAction("/library/watch");
// "Log up to here": the episode and every aired one before it in its season
// that isn't logged yet, in one request.
export const watchThrough = episodeAction("/library/watch-through");
export const unwatchEpisode = episodeAction("/library/unwatch");

// Leave a series' work out of Continue, or put it back. Not queued: it
// never was, and Continue's toast offers Undo while it's in view.
export const skipInSeries = (
  seriesId: number,
  workId: number,
  skip: boolean,
) => {
  const path = `/series/${seriesId}/skips/${workId}`;
  return skip ? api.put(path, undefined) : api.del(path);
};

// The tags on your shelf, most used first.
export const shelfTags = () =>
  api.get<{ tag: string; count: number }[]>("/library/tags");

// More of Home's timeline, after its first page.
export const timelinePage = (offset: number, limit: number) =>
  api.get<TimelinePage>(`/library/timeline?offset=${offset}&limit=${limit}`);

// Send what waited offline, now the device is back (ADR 0005).
export const sendQueued = () => flushQueue();

// The shelf's reads, for a page to show (ADR 0055); each reloads when the
// shelf changes.
const SHELF: Topic[] = ["shelf"];

// Your library, narrowed by status, type or tag; null reads nothing (signed
// out).
export const useShelf = (
  filters: {
    status?: Status | null;
    type?: WorkType | null;
    tag?: string | null;
  } | null,
) => {
  let path: string | null = null;
  if (filters) {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.type) params.set("type", filters.type);
    if (filters.tag) params.set("tag", filters.tag);
    const qs = params.toString();
    path = `/library${qs ? `?${qs}` : ""}`;
  }
  return useApi<Tracking[]>(path, { about: SHELF });
};

// Your entries of a work (each edition's), or of a series' works; null
// reads nothing.
export const useEntriesFor = (workId: number | string | null) =>
  useApi<Tracking[]>(workId === null ? null : `/library?for_work=${workId}`);

export const useSeriesEntries = (seriesId: number | string | null) =>
  useApi<Tracking[]>(
    seriesId === null ? null : `/library?series_id=${seriesId}`,
  );

// Your tags, with how many entries carry each.
export const useShelfTags = () =>
  useApi<{ tag: string; count: number }[]>("/library/tags", { about: SHELF });

// Continue: what's next in each thing you're on.
export const useUpNextList = () =>
  useApi<UpNext[]>("/library/up-next", { about: SHELF });

// Home: the timeline's first page, the wants by type (`preview` of each)
// and the year so far.
export const useTimeline = () =>
  useApi<TimelinePage>("/library/timeline", { about: SHELF });
// Each type's first few wants, for its shelf in Home's Up next.
export const WANTS_PREVIEW = 6;
export const useWants = () =>
  useApi<WantGroup[]>(`/library/wants?preview=${WANTS_PREVIEW}`, {
    about: SHELF,
  });
export const useYear = () =>
  useApi<YearPage>("/library/year", { about: SHELF });

// The works of a series you've said to skip; null reads nothing.
export const useSeriesSkips = (seriesId: number | string | null) =>
  useApi<{ workIds: number[] }>(
    seriesId === null ? null : `/series/${seriesId}/skips`,
  );

// Your reviews of a show's seasons and episodes; null reads nothing.
export const useOwnPartReviews = (workId: number | null) =>
  useApi<PartReview[]>(
    workId === null ? null : `/library/part-reviews?work_id=${workId}`,
  );

// Fetch what a signed-in Home shows while the app's code downloads, so the
// page needn't wait for its chunk to ask (src/data/prefetch.ts).
export const prefetchHome = () => {
  prefetch("/library/timeline");
  prefetch(`/library/wants?preview=${WANTS_PREVIEW}`);
  prefetch("/library/up-next");
};
